import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class EnvironmentService {
  private config: any = {};
  private isLoaded = false;
  private loadPromise: Promise<void> | null = null;

  async load(): Promise<void> {
    if (this.isLoaded) {
      return Promise.resolve();
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this.loadConfig();
    return this.loadPromise;
  }

  private async loadConfig(): Promise<void> {
    const response = await fetch("/api/config");
    if (!response.ok) {
      throw new Error(`Failed to load config from API: ${response.status}`);
    }

    this.config = await response.json();
    this.isLoaded = true;
  }

  setConfig(config: any): void {
    this.config = config;
    this.isLoaded = true;
  }

  get(key: string) {
    // If the key has dots, navigate it as a path
    if (key.includes(".")) {
      const keys = key.split(".");
      let value = this.config;
      for (const k of keys) {
        value = value?.[k];
      }
      return value;
    }

    return this.config[key];
  }

  // Reads a config value as a boolean feature flag. This normalizes the value: only `true` or "true"
  // anything else (including undefined) is `false`, so the app fails safe to the OFF state.
  getFlag(key: string): boolean {
    const value = this.get(key);
    return value === true || value === "true";
  }

  getAll(): any {
    return this.config;
  }

  isConfigLoaded(): boolean {
    return this.isLoaded;
  }

  debugInfo(): void {
    console.log("🐛 Environment debug info:", {
      isLoaded: this.isLoaded,
      hostname: window.location.hostname,
      config: this.config,
    });
  }

  // Factory to create providers for specific tokens.
  // Only for components/services that have not migrated to reading EnvironmentService directly.
  static createCommonProviders(tokens: string[] = ["maps_key"]) {
    const tokenMap: { [key: string]: string } = {
      maps_key: "api.maps_key",
      incode_api_url: "api.incode_url", // Used by IncodeService
    };

    return tokens
      .filter((token) => tokenMap[token])
      .map((token) => ({
        provide: token,
        useFactory: (envService: EnvironmentService) => {
          const path = tokenMap[token];
          const keys = path.split(".");
          let value = envService.getAll();
          for (const key of keys) {
            value = value?.[key];
          }
          return value;
        },
        deps: [EnvironmentService],
      }));
  }
}
